import React, { useMemo } from 'react';
import type {
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getBankImportDefs, saveBankImportDefsBatch } from '../../api/bankImports';
import type { BankImportDefRow } from '../../api/bankImports';
import { useBanks } from '../../hooks/useBanks';
import { t } from '../../i18n/t';

const FORMATS = ['poalim', 'leumi', 'mizrahi', 'fibi', 'discount', 'nesua'];
const IMPORT_TYPES = ['balance', 'transaction'];
const SOURCE_KINDS = ['folder', 'file'];

const newDefaults = (): Partial<BankImportDefRow> => ({
  importId: 0,
  importName: '',
  bankId: 0,
  format: 'discount',
  importType: 'balance',
  sourceKind: 'folder',
  filePattern: null,
  splitterCode: null,
  lastImportDate: null,
  isActive: true,
});

export function BankImportsAdminPage() {
  const { data: banks, loading, error } = useBanks();

  const columnDefs = useMemo<ColDef<BankImportDefRow>[]>(() => {
    if (!banks) return [];

    // bankId → "<id> - <name>" dropdown (required).
    const values = banks.map((b) => `${b.bankId} - ${b.bankName}`);
    const labelById = new Map<number, string>(
      banks.map((b) => [b.bankId, `${b.bankId} - ${b.bankName}`]),
    );
    const idByLabel = new Map<string, number>(
      banks.map((b) => [`${b.bankId} - ${b.bankName}`, b.bankId]),
    );

    const fixed = (field: keyof BankImportDefRow, opts: string[]): ColDef<BankImportDefRow> => ({
      field,
      headerName: t(`bankImports.columns.${field}` as Parameters<typeof t>[0]),
      width: 130,
      editable: true,
      cellDataType: 'text',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: opts },
    });

    return [
      {
        field: 'importId',
        headerName: t('bankImports.columns.importId'),
        width: 80,
        editable: false,
        sort: 'asc',
        valueFormatter: (p: ValueFormatterParams<BankImportDefRow>) => {
          const added = (p.data as { __status?: string } | undefined)?.__status === 'added';
          return added || p.value == null ? '' : String(p.value);
        },
      },
      { field: 'importName', headerName: t('bankImports.columns.importName'), flex: 1, editable: true },
      {
        field: 'bankId',
        headerName: t('bankImports.columns.bankId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values },
        valueGetter: (p: ValueGetterParams<BankImportDefRow>) => {
          const v = p.data?.bankId;
          if (v == null || v === 0) return '';
          return labelById.get(v) ?? String(v);
        },
        valueSetter: (p: ValueSetterParams<BankImportDefRow>) => {
          if (!p.data) return false;
          const id = idByLabel.get(p.newValue);
          if (id === undefined) return false;
          p.data.bankId = id;
          return true;
        },
      },
      fixed('format', FORMATS),
      fixed('importType', IMPORT_TYPES),
      fixed('sourceKind', SOURCE_KINDS),
      { field: 'filePattern', headerName: t('bankImports.columns.filePattern'), width: 160, editable: true },
      { field: 'splitterCode', headerName: t('bankImports.columns.splitterCode'), width: 110, editable: true },
      { field: 'lastImportDate', headerName: t('bankImports.columns.lastImportDate'), width: 150, editable: false },
      { field: 'lastValueDate', headerName: t('bankImports.columns.lastValueDate'), width: 150, editable: false },
      { field: 'isActive', headerName: t('bankImports.columns.isActive'), width: 90, editable: true, cellDataType: 'boolean' },
    ];
  }, [banks]);

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'var(--color-error-text)', background: 'var(--color-error-bg)', padding: '8px 12px', borderRadius: 4 }}>{error}</div>
      </div>
    );
  }
  if (loading || !banks) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <LopCrudPage<BankImportDefRow>
      resourceCode="bankFiles"
      titleKey="bankImports.pageTitle"
      idField="importId"
      getRows={getBankImportDefs}
      saveBatch={(added, modified, deletedIds) =>
        saveBankImportDefsBatch(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newDefaults}
    />
  );
}
